import type { ShouldRevalidateFunctionArgs } from "react-router";

import { isLogoutSubmit } from "~/routes/auth-revalidate";

/**
 * Pure trigger behind `<SegmentLogoutReset />` (./logout-reset.tsx) —
 * extracted to its own module (react-refresh wants component files
 * component-only) so the rules are unit-testable without React. Gated on the
 * submission being the sign-out via the shared `isLogoutSubmit` predicate, so
 * "what counts as a logout" cannot drift from the root/layout/dispatcher
 * revalidation gates.
 *
 * Fires at submission START (PR #1084 review): the reset must land BEFORE the
 * action's redirect kicks off the revalidation GETs, so those go out without
 * the ajs cookies and the server rebuilds `hec_pz`/`pzHits` as guest. When the
 * "submitting" snapshot arrives the logout POST is already in flight (and
 * needs no Segment identity), so resetting here races nothing. Firing at
 * nav-idle instead let the whole redirect+revalidation wave carry the former
 * customer's `ajs_user_id`.
 *
 * `fired` holds until the navigation settles back to idle: repeated
 * "submitting" snapshots, the post-action "loading" phase, and an
 * interrupting navigation all keep it latched, so the reset is exactly-once
 * per sign-out. A later, separate logout (idle in between) fires again.
 */
export interface LogoutNavSnapshot
  extends Pick<ShouldRevalidateFunctionArgs, "formMethod" | "formAction"> {
  state: "idle" | "loading" | "submitting";
}

export function computeLogoutResetTrigger(
  prev: { fired: boolean },
  nav: LogoutNavSnapshot,
): { fire: boolean; next: { fired: boolean } } {
  if (nav.state === "idle") {
    return { fire: false, next: { fired: false } };
  }
  if (nav.state === "submitting" && isLogoutSubmit(nav)) {
    return { fire: !prev.fired, next: { fired: true } };
  }
  // Post-action "loading" (redirect/revalidations) or an unrelated nav —
  // hold the marker until idle so nothing double-fires.
  return { fire: false, next: prev };
}

/**
 * BACKSTOP (defense-in-depth ONLY — jam-4 round 2, scope-guarded by JP: its
 * one-request lag must never justify weakening a primary flow's
 * reset-before-navigation ordering): fire the client Segment reset when the
 * root loader's session `hashedEmail` transitions present→absent while an
 * `ajs_user_id` cookie is STILL on the browser — i.e. some server-side
 * teardown ran without the client reset (a future fetcher path missing the
 * explicit `forcedLogout` signal). The cookie-present guard makes it a
 * no-op after a normal /logout (the submit-start reset already cleared the
 * cookie by the time the revalidation data lands) and keeps
 * `analytics.reset()`'s anonymous-id rotation off the guest path.
 */
export function computeIdentityDowngradeReset(
  prev: { hadHashedEmail: boolean },
  args: { hashedEmail: string | null; ajsUserIdPresent: boolean },
): { fire: boolean; next: { hadHashedEmail: boolean } } {
  const hasHashedEmail = Boolean(args.hashedEmail);
  return {
    fire: prev.hadHashedEmail && !hasHashedEmail && args.ajsUserIdPresent,
    next: { hadHashedEmail: hasHashedEmail },
  };
}
